import { motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";

const EASE = [0.22, 1, 0.36, 1];

export const Counter = ({ value }) => {
  const match = String(value).match(/^([\d.]+)(.*)$/);
  const target = match ? parseFloat(match[1]) : 0;
  const suffix = match ? match[2] : String(value);
  const decimals = match && match[1].includes(".") ? 1 : 0;
  const [n, setN] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting && !started.current) {
          started.current = true;
          const dur = 1400;
          const t0 = performance.now();
          const tick = (t) => {
            const p = Math.min((t - t0) / dur, 1);
            const eased = 1 - Math.pow(1 - p, 3);
            setN(target * eased);
            if (p < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
        }
      },
      { threshold: 0.5 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [target]);

  return (
    <span ref={ref}>
      {n.toFixed(decimals)}
      {suffix}
    </span>
  );
};

export const Reveal = ({ children, delay = 0, y = 30, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.7, ease: EASE, delay }}
    className={className}
  >
    {children}
  </motion.div>
);

export const DecryptText = ({ text, className = "" }) => {
  const [out, setOut] = useState(text);
  const ref = useRef(null);
  const started = useRef(false);
  const CH = "ABCDEF0123456789#%&/<>*!";
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Re-run when text (language) changes so already-revealed labels update.
    started.current = false;
    setOut(text);
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting && !started.current) {
          started.current = true;
          let frame = 0;
          const id = setInterval(() => {
            frame += 1;
            const revealed = Math.floor(frame / 2);
            let s = "";
            for (let i = 0; i < text.length; i++) {
              if (i < revealed || text[i] === " ") s += text[i];
              else s += CH[Math.floor(Math.random() * CH.length)];
            }
            setOut(s);
            if (revealed >= text.length) {
              clearInterval(id);
              setOut(text);
            }
          }, 38);
        }
      },
      { threshold: 0.6 }
    );
    io.observe(el);
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);
  return <span ref={ref} className={className}>{out}</span>;
};

export const SectionLabel = ({ index, children }) => (
  <div className="mb-5 flex items-center gap-3">
    <span className="font-mono text-xs font-bold text-cyan-neon">{index}</span>
    <span className="h-px w-8 bg-cyan-neon/40" />
    <DecryptText
      text={children}
      className="font-mono text-[11px] uppercase tracking-[0.3em] text-white/50"
    />
  </div>
);
